import type { LogOverviewRow } from '@/api/hooks/useIntegrationLogs'
import type { IHeaderParams, IRowNode } from 'ag-grid-community'
import { Checkbox, Tooltip } from 'antd'
import { useCallback, useEffect, useState } from 'react'
import { allNodesSelected, nodesOnPage } from './pageSelection'

/**
 * Replacement for AG-Grid's selection header, because it doesn't guarantee only one page of selected results.
 */
export function SelectPageHeader({ api }: IHeaderParams<LogOverviewRow>) {
  const [checked, setChecked] = useState(false)
  const [indeterminate, setIndeterminate] = useState(false)

  const pageNodes = useCallback((): IRowNode<LogOverviewRow>[] => {
    const loaded: IRowNode<LogOverviewRow>[] = []
    api.forEachNode((node) => loaded.push(node))
    return nodesOnPage<IRowNode<LogOverviewRow>>(
      loaded,
      api.paginationGetCurrentPage(),
      api.paginationGetPageSize(),
    )
  }, [api])

  const sync = useCallback(() => {
    const nodes = pageNodes()
    const selected = nodes.filter((node) => node.isSelected()).length
    setChecked(nodes.length > 0 && selected === nodes.length)
    setIndeterminate(selected > 0 && selected < nodes.length)
  }, [pageNodes])

  useEffect(() => {
    sync()
    const events = ['selectionChanged', 'paginationChanged', 'modelUpdated']
    for (const event of events) api.addEventListener(event, sync)
    return () => {
      for (const event of events) api.removeEventListener(event, sync)
    }
  }, [api, sync])

  const toggle = useCallback(() => {
    const nodes = pageNodes()
    if (nodes.length === 0) return
    api.setNodesSelected({
      nodes,
      newValue: !allNodesSelected(nodes),
      source: 'api',
    })
  }, [api, pageNodes])

  return (
    <Tooltip title='Select all on this page' placement='top'>
      <Checkbox
        aria-label='Select all on this page'
        checked={checked}
        indeterminate={indeterminate}
        onChange={toggle}
      />
    </Tooltip>
  )
}
